import { Stack, useMediaQuery } from '@mui/material'
import { useRouter } from 'next/router'
import { useMemo, useState, type ReactNode } from 'react'
import { APP_SCROLLABLE_WRAPPER_ID, mainWrapperContainerName, PAGE_SURFACE_VAR } from '@omnea-app/fe-app-shell/AppLayout'
import { ShellContext } from '@omnea-app/fe-app-shell/shell/ShellContext'
import { ReactErrorBoundary } from '@omnea-app/fe-legacy/utils/error-boundary/ReactErrorBoundary'
import { ShellSettingsNav } from './ShellSettingsNav'
import { ShellSideNav } from './ShellSideNav'
import { ShellTopBarControls } from './ShellTopBarControls'

type AppShellFrameProps = {
  children: ReactNode
}

/** The app shell around every page: the nav and the scrolling page, whose header draws the top bar. */
export const AppShellFrame = ({ children }: AppShellFrameProps) => {
  const { pathname, asPath } = useRouter()
  // The frame only mounts on the client once the shell variant is known, so reading the width up front is safe.
  const isNarrow = useMediaQuery((theme) => theme.breakpoints.down('lg'), { noSsr: true })
  const [userCollapsed, setUserCollapsed] = useState<boolean | null>(null)
  const [prevIsNarrow, setPrevIsNarrow] = useState(isNarrow)
  // The window's width decides until the user toggles; crossing the breakpoint hands it back to the width.
  if (isNarrow !== prevIsNarrow) {
    setPrevIsNarrow(isNarrow)
    setUserCollapsed(null)
  }
  const isNavCollapsed = userCollapsed ?? isNarrow

  const shell = useMemo(() => ({ topBarTrailing: <ShellTopBarControls /> }), [])

  return (
    <ShellContext.Provider value={shell}>
      <Stack direction="row" sx={{ flex: 1, height: '100%', minWidth: 0 }}>
        {pathname.startsWith('/settings') ? (
          <ShellSettingsNav />
        ) : (
          <ShellSideNav collapsed={isNavCollapsed} onToggleCollapsed={() => setUserCollapsed(!isNavCollapsed)} />
        )}
        <Stack
          // TODO(SOUR-1884): render a `main` element once pages stop rendering their own.
          role="main"
          id={APP_SCROLLABLE_WRAPPER_ID}
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: 'bg.surface.default',
            [PAGE_SURFACE_VAR]: (theme) => theme.palette.bg.surface.default,
            // Sub-components use this for container queries.
            containerName: mainWrapperContainerName,
            containerType: 'inline-size',
            overflowX: 'clip',
            overflowY: 'auto',
          }}
        >
          {/* Per page, so a page that crashes leaves the nav to leave it by. */}
          <ReactErrorBoundary resetKey={asPath}>{children}</ReactErrorBoundary>
        </Stack>
      </Stack>
    </ShellContext.Provider>
  )
}
